/**
 * Robot Alpha (super-admin) — the robot that drives the Alpha DMS UI on the garage server.
 *
 * Status card from the proxy heartbeat (servicebox.alpha_robot, proxy >= 3.64.0), then the
 * timeline of the commands it ran (proxy_query_results). A click opens one command: its
 * steps, the values before/after, the Alpha windows it answered, and the pictures it took.
 * Refreshes every 15 s.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  proxyAdminApi,
  type AlphaRobotCommand,
  type AlphaRobotCommandDetail,
  type AlphaRobotCapture,
  type AlphaRobotImage,
  type AlphaRobotScreen,
  type AlphaRobotStatus,
} from "@services/api/proxyAdmin";
import { showNotification } from "context/notification.service";

const HERIC_ORG_ID = "Jw4uF0bN40IYdSIQBIqr";
const REFRESH_MS = 15000;
const PAGE = 50;
const CAPTURE_PAGE = 24;
const SCREEN_EVERY_MS = 30000;

const OP_LABELS: Record<string, string> = {
  windows: "Fenêtres",
  batch: "Enchaînement",
  client: "Lecture fiche",
  open_client: "Ouvrir fiche",
  update: "Modifier fiche",
  save: "Enregistrer",
  set: "Saisie",
  select: "Liste",
  invoke: "Bouton",
  keys: "Touches",
  tab: "Onglet",
  tree: "Arborescence",
  read: "Lecture",
  capture: "Capture",
  clipboard: "Presse-papiers",
  search_confirm: "Recherche",
  self_update: "Mise à jour robot",
  fetch: "Téléchargement robot",
  find_client: "Recherche client",
  create_client: "Création client",
  invoice_pdf: "Facture PDF",
};

function opLabel(op: string): string {
  return OP_LABELS[op] || op;
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

function fmtAgo(iso?: string | null): string {
  if (!iso) return "";
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (isNaN(s)) return "";
  if (s < 60) return `il y a ${Math.max(s, 0)} s`;
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)} h`;
  return `il y a ${Math.round(s / 86400)} j`;
}

function fmtMs(ms?: number | null): string {
  if (ms == null) return "";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === "") return "∅";
  return typeof v === "string" ? v : JSON.stringify(v);
}

// Results never change: one object URL per picture for the life of the page.
const imageUrls = new Map<string, Promise<string>>();

function loadImage(orgId: string, cmdId: string, index: number): Promise<string> {
  const key = `${orgId}/${cmdId}/${index}`;
  let p = imageUrls.get(key);
  if (!p) {
    p = proxyAdminApi.getAlphaRobotImageUrl(orgId, cmdId, index);
    p.catch(() => imageUrls.delete(key));
    imageUrls.set(key, p);
  }
  return p;
}

/** The picture is fetched when it scrolls into view (each one is 100–500 KB). */
function RobotImg({ orgId, cmdId, index, alt, className, eager }: {
  orgId: string; cmdId: string; index: number; alt: string; className?: string; eager?: boolean;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(!!eager);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible || !ref.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setVisible(true);
    }, { rootMargin: "300px" });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let alive = true;
    setFailed(false);
    loadImage(orgId, cmdId, index)
      .then((u) => alive && setSrc(u))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [visible, orgId, cmdId, index]);

  if (src) return <img src={src} alt={alt} className={className} />;
  return (
    <div ref={ref} className={`${className || ""} bg-neutral-100 flex items-center justify-center text-h5 text-neutral-400 min-h-100 min-w-150`}>
      {failed ? "Capture illisible" : "Chargement…"}
    </div>
  );
}

type Zoom = { orgId: string; cmdId: string; index: number; label: string };

function Lightbox({ zoom, onClose }: { zoom: Zoom; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[10001] bg-black/80 flex flex-col items-center justify-center gap-3 p-6 cursor-zoom-out"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <RobotImg orgId={zoom.orgId} cmdId={zoom.cmdId} index={zoom.index} alt={zoom.label} eager className="max-w-full max-h-[90vh] rounded-lg shadow-2xl" />
      <span className="text-h4 text-white/80">{zoom.label}</span>
    </div>
  );
}

/** Which command the pictures of a detail belong to. */
const ImgCtx = createContext<{ orgId: string; cmdId: string }>({ orgId: "", cmdId: "" });

function captureLabel(img: AlphaRobotImage): string {
  const where = img.where === "réponse" ? "capture finale" : img.where;
  return img.window ? `${img.window} (${where})` : where;
}

function Pill({ tone, children }: { tone: "green" | "red" | "grey" | "amber"; children: React.ReactNode }) {
  const cls = {
    green: "bg-green-50 text-green-700 border-green-200",
    red: "bg-red-50 text-red-700 border-red-200",
    grey: "bg-neutral-100 text-neutral-600 border-neutral-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  }[tone];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-h5 font-medium whitespace-nowrap ${cls}`}>{children}</span>;
}

function Field({ label, value, hint }: { label: string; value: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-150">
      <span className="text-h5 text-neutral-400">{label}</span>
      <span className="text-h3 font-medium text-neutral-800">{value}</span>
      {hint && <span className="text-h5 text-neutral-400">{hint}</span>}
    </div>
  );
}

function loginTone(login: string): "green" | "red" | "grey" {
  const l = login.toLowerCase();
  if (l.startsWith("ok")) return "green";
  if (l.startsWith("echec") || l.startsWith("échec") || l.startsWith("bloque") || l.startsWith("bloqué")) return "red";
  return "grey";
}

/** "ok 2026-10-17T13:00:00+00:00" → "ok — 17/10 15:00:00". */
function loginLabel(login: string): string {
  const m = login.match(/^ok\s+(\S+)$/i);
  return m ? `OK — ${fmtDateTime(m[1])}` : login;
}

function SessionsTable({ sessions }: { sessions: NonNullable<AlphaRobotStatus["robot"]>["sessions"] }) {
  const list = sessions || [];
  return (
    <div className="flex flex-col gap-1">
      <span className="text-h5 text-neutral-400">Sessions Windows sur le serveur ({list.length})</span>
      {list.length === 0 ? (
        <span className="text-h4 text-neutral-400">Aucune</span>
      ) : (
        <table className="text-h4 w-fit">
          <thead>
            <tr className="text-neutral-400 text-h5">
              <th className="text-left pr-6 font-normal">Utilisateur</th>
              <th className="text-left pr-6 font-normal">État</th>
              <th className="text-left pr-6 font-normal">Poste</th>
              <th className="text-left font-normal">Id</th>
            </tr>
          </thead>
          <tbody>
            {list.map((x, i) => (
              <tr key={`${x.id}-${i}`}>
                <td className="pr-6 font-medium text-neutral-700">{x.user || "—"}</td>
                <td className={`pr-6 ${String(x.state || "").toLowerCase().startsWith("act") ? "text-green-700" : "text-neutral-500"}`}>
                  {x.state || "—"}
                </td>
                <td className="pr-6 text-neutral-500">{x.station || "—"}</td>
                <td className="text-neutral-500">{x.id ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatusCard({ status, latest, hidePendingId }: { status: AlphaRobotStatus | null; latest?: AlphaRobotCommand; hidePendingId?: string | null }) {
  if (!status) return null;
  const pending = status.pending.filter((p) => p.id !== hidePendingId);
  const r = status.robot;
  const down = status.reported && status.up === false;
  const border = down ? "border-red-300 bg-red-50/40" : "border-neutral-200 bg-white";
  const fallback = latest?.robot;
  return (
    <div className={`border rounded-xl p-4 flex flex-col gap-4 ${border}`}>
      <div className="flex flex-row items-center gap-3 flex-wrap">
        <span className="text-h2 font-semibold text-neutral-700">État du robot</span>
        {!status.reported && <Pill tone="grey">Pas encore remonté</Pill>}
        {status.reported && status.up && <Pill tone="green">En ligne</Pill>}
        {status.reported && status.stale && <Pill tone="amber">État périmé</Pill>}
        {down && !status.stale && <Pill tone="red">Hors service</Pill>}
        {pending.length > 0 && <Pill tone="amber">{pending.length} commande(s) en attente</Pill>}
      </div>
      {!status.reported && (
        <div className="text-h4 text-neutral-500">
          Le proxy n'envoie l'état du robot qu'à partir de la version 3.64.0 (proxy actuel : {status.proxy_version || "?"}).
          {fallback && (
            <> Dernière commande exécutée par le robot {fallback.version} ({fallback.user}@{fallback.machine}), {fmtAgo(latest?.executed_at)}.</>
          )}
        </div>
      )}
      <div className="flex flex-row flex-wrap gap-6">
        {r && (
          <>
            <Field label="Robot actif" value={r.alive ? "Oui" : <span className="text-red-600">Non</span>} />
            <Field
              label="Alpha ouvert"
              value={r.alpha_open === false ? <span className="text-red-600">Non</span> : r.alpha_open ? "Oui" : "?"}
            />
            <Field label="Version" value={r.version || "—"} />
            <Field label="Session" value={r.user || "—"} />
            <Field label="Écriture" value={r.allow_writes ? "Autorisée" : "Lecture seule"} />
            <Field label="Dernier battement" value={fmtDateTime(r.heartbeat_at)} hint={fmtAgo(r.heartbeat_at)} />
            <Field label="Démarré" value={fmtDateTime(r.started_at)} />
            {r.down_since && (
              <Field label="En panne depuis" value={<span className="text-red-600">{fmtDateTime(r.down_since)}</span>} hint={fmtAgo(r.down_since)} />
            )}
          </>
        )}
        {!r && fallback && (
          <>
            <Field label="Version (dernière commande)" value={fallback.version || "—"} />
            <Field label="Session" value={`${fallback.user || "?"}@${fallback.machine || "?"}`} />
            <Field label="Écriture" value={fallback.allow_writes ? "Autorisée" : "Lecture seule"} />
          </>
        )}
        <Field label="Proxy" value={status.proxy_version || "—"} hint={status.proxy_last_seen ? `vu ${fmtAgo(status.proxy_last_seen)}` : undefined} />
        {status.fast_heartbeat_until && new Date(status.fast_heartbeat_until).getTime() > Date.now() && (
          <Field label="Mode travaux jusqu'à" value={fmtDateTime(status.fast_heartbeat_until)} />
        )}
      </div>
      {r && (r.alpha_login != null || r.alpha_crash || r.alpha_processes != null || r.alpha_processes_server != null || r.alpha_duplicates) && (
        <div className="flex flex-row flex-wrap gap-6">
          {r.alpha_login != null && (
            <Field label="Connexion Alpha" value={<Pill tone={loginTone(r.alpha_login || "")}>{loginLabel(r.alpha_login || "—")}</Pill>} />
          )}
          {r.alpha_processes != null && (
            <Field
              label="Alpha dans la session du robot"
              value={<span className={r.alpha_processes > 1 ? "text-amber-600" : ""}>{r.alpha_processes}</span>}
              hint={r.alpha_processes > 1 ? "plusieurs (normal quelques secondes après la connexion)" : undefined}
            />
          )}
          {r.alpha_processes_server != null && <Field label="Alpha sur tout le serveur" value={r.alpha_processes_server} />}
          {r.alpha_crash && (
            <Field
              label="Dernier plantage"
              value={<span className="text-red-600">{fmtDateTime(r.alpha_crash.at)}</span>}
              hint={[r.alpha_crash.reason, fmtAgo(r.alpha_crash.at)].filter(Boolean).join(" · ")}
            />
          )}
          {r.alpha_duplicates && (
            <Field
              label="Doublons Alpha"
              value={<span className="text-amber-600">{r.alpha_duplicates.count ?? "?"}</span>}
              hint={r.alpha_duplicates.at ? fmtAgo(r.alpha_duplicates.at) : undefined}
            />
          )}
        </div>
      )}
      {r?.sessions && <SessionsTable sessions={r.sessions} />}
      {pending.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-h5 text-neutral-400">En attente du proxy</span>
          {pending.map((p) => (
            <span key={p.id} className="text-h4 text-neutral-600">
              {opLabel(p.op)} — mise en file {fmtAgo(p.queued_at)}
              {p.attempts ? ` (${p.attempts} essai(s))` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Thumb({ img, onOpen }: { img: AlphaRobotImage; onOpen: (img: AlphaRobotImage) => void }) {
  const { orgId, cmdId } = useContext(ImgCtx);
  return (
    <button type="button" onClick={() => onOpen(img)} className="flex flex-col gap-1 items-start text-left group">
      <RobotImg
        orgId={orgId}
        cmdId={cmdId}
        index={img.index}
        alt={captureLabel(img)}
        className="max-h-200 max-w-full rounded-lg border border-neutral-200 group-hover:ring-2 group-hover:ring-indigo-300"
      />
      <span className="text-h5 text-neutral-400">
        {captureLabel(img)} · {Math.round(img.size_bytes / 1024)} Ko
      </span>
    </button>
  );
}

type Dialog = { title?: string; kind?: string; answer?: string; buttons?: string[]; capture_image?: number };
type Change = { field?: string; before?: unknown; after?: unknown };

function DialogsTable({ dialogs, images, onOpen }: { dialogs: Dialog[]; images: AlphaRobotImage[]; onOpen: (i: AlphaRobotImage) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-h5 text-neutral-400">Fenêtres d'Alpha traitées</span>
      {dialogs.map((d, i) => (
        <div key={i} className="flex flex-col gap-1 border-l-2 border-neutral-200 pl-2">
          <span className="text-h4 text-neutral-700">
            <span className="font-medium">{d.kind || "?"}</span> — {d.title || ""}
            {d.answer && <> → réponse <span className="font-medium">« {d.answer} »</span></>}
          </span>
          {d.buttons && <span className="text-h5 text-neutral-400">Boutons : {d.buttons.join(", ")}</span>}
          {d.capture_image != null && images[d.capture_image] && <Thumb img={images[d.capture_image]} onOpen={onOpen} />}
        </div>
      ))}
    </div>
  );
}

function ChangesTable({ changes }: { changes: Change[] }) {
  return (
    <table className="text-h4 w-fit">
      <thead>
        <tr className="text-neutral-400 text-h5">
          <th className="text-left pr-4 font-normal">Champ</th>
          <th className="text-left pr-4 font-normal">Avant</th>
          <th className="text-left font-normal">Après</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((c, i) => (
          <tr key={i}>
            <td className="pr-4 font-medium text-neutral-700">{c.field}</td>
            <td className="pr-4 text-neutral-500">{show(c.before)}</td>
            <td className="text-neutral-800">{show(c.after)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const STRUCTURED = new Set(["op", "error", "before", "after", "changes", "handled", "warnings", "dialog", "capture_image", "client", "readback", "mismatch", "windows", "capture_path"]);

/** One step of a batch, or the whole response of a single operation. */
function StepView({ step, images, onOpen }: { step: Record<string, unknown>; images: AlphaRobotImage[]; onOpen: (i: AlphaRobotImage) => void }) {
  const rest = Object.entries(step).filter(([k]) => !STRUCTURED.has(k));
  const handled = (step.handled as Dialog[] | undefined) || [];
  const dialog = step.dialog as Dialog | undefined;
  const changes = (step.changes as Change[] | undefined) || [];
  const client = step.client as Record<string, unknown> | undefined;
  const readback = step.readback as Record<string, unknown> | undefined;
  const mismatch = step.mismatch as Record<string, { wanted?: unknown; read?: unknown }> | undefined;
  const windows = step.windows as { title?: string }[] | undefined;
  return (
    <div className="flex flex-col gap-2">
      {typeof step.error === "string" && step.error && <div className="text-h4 text-red-600">{step.error}</div>}
      {("before" in step || "after" in step) && (
        <div className="text-h4 text-neutral-700">
          Avant <span className="font-medium">{show(step.before)}</span> → après <span className="font-medium">{show(step.after)}</span>
        </div>
      )}
      {changes.length > 0 && <ChangesTable changes={changes} />}
      {readback && (
        <div className="text-h4 text-neutral-600">
          Relu : {Object.entries(readback).map(([k, v]) => `${k} = ${show(v)}`).join(" · ")}
        </div>
      )}
      {mismatch && Object.keys(mismatch).length > 0 && (
        <div className="text-h4 text-red-600">
          Écart : {Object.entries(mismatch).map(([k, v]) => `${k} voulu ${show(v.wanted)}, relu ${show(v.read)}`).join(" · ")}
        </div>
      )}
      {client && typeof client === "object" && (
        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 w-fit text-h4">
          {Object.entries(client).map(([k, v]) => (
            <div key={k} className="contents">
              <span className="text-neutral-400">{k}</span>
              <span className="text-neutral-800">{show(v)}</span>
            </div>
          ))}
        </div>
      )}
      {windows && (
        <ul className="text-h4 text-neutral-600 list-disc pl-5">
          {windows.map((w, i) => (
            <li key={i}>{w.title}</li>
          ))}
        </ul>
      )}
      {handled.length > 0 && <DialogsTable dialogs={handled} images={images} onOpen={onOpen} />}
      {dialog && (
        <div className="flex flex-col gap-1">
          <span className="text-h5 text-red-500">Fenêtre non prévue</span>
          <DialogsTable dialogs={[dialog]} images={images} onOpen={onOpen} />
        </div>
      )}
      {rest.length > 0 && (
        <div className="text-h5 text-neutral-500 font-mono break-all">
          {rest.map(([k, v]) => `${k}: ${show(v)}`).join("   ")}
        </div>
      )}
      {typeof step.capture_image === "number" && images[step.capture_image] && <Thumb img={images[step.capture_image]} onOpen={onOpen} />}
    </div>
  );
}

function DetailModal({ orgId, cmdId, onClose }: { orgId: string; cmdId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<AlphaRobotCommandDetail | null>(null);
  const [zoom, setZoom] = useState<AlphaRobotImage | null>(null);
  const [raw, setRaw] = useState(false);

  useEffect(() => {
    let alive = true;
    proxyAdminApi
      .getAlphaRobotCommand(orgId, cmdId)
      .then((d) => alive && setDetail(d))
      .catch((e) => showNotification({ type: "error", message: e?.response?.data?.detail || "Lecture du résultat impossible" }));
    return () => {
      alive = false;
    };
  }, [orgId, cmdId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (zoom) setZoom(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom, onClose]);

  const resp = (detail?.response || {}) as Record<string, unknown>;
  const steps = (resp.steps as Record<string, unknown>[] | undefined) || null;
  const images = detail?.images || [];
  const top: Record<string, unknown> = steps ? Object.fromEntries(Object.entries(resp).filter(([k]) => k !== "steps")) : resp;
  const topShown = Object.fromEntries(
    Object.entries(top).filter(([k]) => !["ok", "robot", "robot_version", "allow_writes", "elapsed_ms", "rect", "nodes", "fields"].includes(k)),
  );

  return (
    <ImgCtx.Provider value={{ orgId, cmdId }}>
    <div className="fixed inset-0 z-[10000] bg-black/40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-900 h-full overflow-y-auto p-6 flex flex-col gap-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xl font-semibold text-neutral-800">
              {detail ? opLabel(detail.op) : "Chargement…"}
            </span>
            {detail && (
              <span className="text-h4 text-neutral-500">
                {fmtDateTime(detail.executed_at)} · {detail.hostname || "?"} · proxy {detail.proxy_version || "?"}
                {detail.robot?.version ? ` · robot ${detail.robot.version}` : ""}
                {detail.elapsed_ms != null ? ` · ${fmtMs(detail.elapsed_ms)}` : ""}
              </span>
            )}
          </div>
          <div className="flex flex-row items-center gap-3">
            {detail && (detail.ok ? <Pill tone="green">OK</Pill> : <Pill tone="red">Erreur</Pill>)}
            <button type="button" onClick={onClose} className="text-h3 text-neutral-500 hover:text-neutral-800 px-2">
              ✕
            </button>
          </div>
        </div>
        {detail && (
          <>
            {detail.error && <div className="text-h3 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 break-words">{detail.error}</div>}
            <div className="flex flex-col gap-1">
              <span className="text-h5 text-neutral-400">Commande envoyée</span>
              <pre className="text-h5 bg-neutral-50 border border-neutral-200 rounded-lg p-3 whitespace-pre-wrap break-all">
                {JSON.stringify(detail.payload, null, 2)}
              </pre>
            </div>
            {steps && (
              <div className="flex flex-col gap-3">
                <span className="text-h2 font-semibold text-neutral-700">Étapes</span>
                {steps.map((st, i) => (
                  <div key={i} className={`border rounded-lg p-3 flex flex-col gap-2 ${st.error ? "border-red-200 bg-red-50/30" : "border-neutral-200"}`}>
                    <div className="flex flex-row items-center gap-2">
                      <span className="text-h5 text-neutral-400">{i + 1}.</span>
                      <span className="text-h3 font-medium text-neutral-800">{opLabel(String(st.op || "?"))}</span>
                      {st.error ? <Pill tone="red">Arrêt</Pill> : <Pill tone="green">OK</Pill>}
                    </div>
                    <StepView step={st} images={images} onOpen={setZoom} />
                  </div>
                ))}
              </div>
            )}
            {Object.keys(topShown).length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-h2 font-semibold text-neutral-700">{steps ? "Fin de l'enchaînement" : "Résultat"}</span>
                <StepView step={topShown} images={images} onOpen={setZoom} />
              </div>
            )}
            {images.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-h2 font-semibold text-neutral-700">Captures ({images.length})</span>
                <div className="flex flex-row flex-wrap gap-4">
                  {images.map((img) => (
                    <Thumb key={img.index} img={img} onOpen={setZoom} />
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <button type="button" onClick={() => setRaw((v) => !v)} className="text-h4 text-indigo-600 w-fit hover:underline">
                {raw ? "Masquer la réponse brute" : "Voir la réponse brute"}
              </button>
              {raw && (
                <pre className="text-h5 bg-neutral-50 border border-neutral-200 rounded-lg p-3 whitespace-pre-wrap break-all">
                  {JSON.stringify(detail.response, null, 2)}
                </pre>
              )}
            </div>
          </>
        )}
      </div>
      {zoom && <Lightbox zoom={{ orgId, cmdId, index: zoom.index, label: captureLabel(zoom) }} onClose={() => setZoom(null)} />}
    </div>
    </ImgCtx.Provider>
  );
}

/** "As if the PC were open": the latest full-screen picture of the robot's session. */
function ScreenCard({ orgId, screen, requesting, onRefresh, onZoom }: {
  orgId: string;
  screen: AlphaRobotScreen | null;
  requesting: boolean;
  onRefresh: () => void;
  onZoom: (z: Zoom) => void;
}) {
  const cap = screen?.capture;
  const failed = screen?.last_attempt && !screen.last_attempt.ok && screen.last_attempt.cmd_id !== cap?.cmd_id ? screen.last_attempt : null;
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5">
          <span className="text-h2 font-semibold text-neutral-700">Écran du serveur</span>
          <span className="text-h4 text-neutral-500">
            {cap ? `${fmtDateTime(cap.executed_at)} · ${fmtAgo(cap.executed_at)}` : screen ? "Aucune capture d'écran pour l'instant" : "Chargement…"}
            {" · "}nouvelle image toutes les 30 s tant que la page est ouverte
          </span>
        </div>
        <div className="flex flex-row items-center gap-3">
          {(requesting || screen?.pending_id) && <Pill tone="amber">Capture demandée…</Pill>}
          <button
            type="button"
            onClick={onRefresh}
            disabled={requesting}
            className="px-3 py-1 rounded-lg border border-neutral-200 text-h4 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            Rafraîchir
          </button>
        </div>
      </div>
      {failed && (
        <div className="text-h4 text-red-600">
          Dernière demande en échec ({fmtAgo(failed.executed_at)}) : {failed.error || "erreur inconnue"}
        </div>
      )}
      {cap && (
        <button
          type="button"
          className="w-full cursor-zoom-in"
          onClick={() => onZoom({ orgId, cmdId: cap.cmd_id, index: cap.index, label: `Écran du serveur — ${fmtDateTime(cap.executed_at)}` })}
        >
          <RobotImg
            key={`${cap.cmd_id}/${cap.index}`}
            orgId={orgId}
            cmdId={cap.cmd_id}
            index={cap.index}
            alt="Écran du serveur"
            eager
            className="w-full h-auto rounded-lg border border-neutral-200 bg-neutral-900"
          />
        </button>
      )}
    </div>
  );
}

function LastCapture({ orgId, capture, onZoom, onOpenCommand }: {
  orgId: string;
  capture: AlphaRobotCapture | null | undefined;
  onZoom: (z: Zoom) => void;
  onOpenCommand: (id: string) => void;
}) {
  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-3">
      <span className="text-h2 font-semibold text-neutral-700">Dernière capture</span>
      {capture === undefined && <span className="text-h4 text-neutral-400">Chargement…</span>}
      {capture === null && <span className="text-h4 text-neutral-400">Le robot n'a encore pris aucune capture.</span>}
      {capture && (
        <>
          <button
            type="button"
            className="w-full cursor-zoom-in"
            onClick={() => onZoom({ orgId, cmdId: capture.cmd_id, index: capture.index, label: captureLabel(capture) })}
          >
            <RobotImg
              key={`${capture.cmd_id}/${capture.index}`}
              orgId={orgId}
              cmdId={capture.cmd_id}
              index={capture.index}
              alt={captureLabel(capture)}
              eager
              className="w-full max-h-[480px] object-contain object-left-top rounded-lg border border-neutral-200 bg-neutral-50"
            />
          </button>
          <div className="flex flex-row items-start justify-between gap-3 flex-wrap">
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-h3 font-medium text-neutral-800 break-words">{capture.window || "Fenêtre inconnue"}</span>
              <span className="text-h4 text-neutral-500">
                {fmtDateTime(capture.executed_at)} · {fmtAgo(capture.executed_at)}
              </span>
              <span className={`text-h4 ${capture.ok ? "text-neutral-500" : "text-red-600"}`}>
                {opLabel(capture.op)} — {capture.summary}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onOpenCommand(capture.cmd_id)}
              className="px-3 py-1 rounded-lg border border-neutral-200 text-h4 text-indigo-600 hover:bg-neutral-50 shrink-0"
            >
              Ouvrir la commande
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CapturesGallery({ orgId, captures, hasMore, onMore, onZoom, onOpenCommand }: {
  orgId: string;
  captures: AlphaRobotCapture[];
  hasMore: boolean;
  onMore: () => void;
  onZoom: (z: Zoom) => void;
  onOpenCommand: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-3 p-4">
      {captures.length === 0 && <span className="text-h4 text-neutral-400">Aucune capture.</span>}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {captures.map((c) => (
          <div key={`${c.cmd_id}/${c.index}`} className="flex flex-col gap-1 border border-neutral-200 rounded-lg p-2">
            <button
              type="button"
              className="cursor-zoom-in"
              onClick={() => onZoom({ orgId, cmdId: c.cmd_id, index: c.index, label: captureLabel(c) })}
            >
              <RobotImg
                orgId={orgId}
                cmdId={c.cmd_id}
                index={c.index}
                alt={captureLabel(c)}
                className="w-full h-150 object-cover object-left-top rounded-md"
              />
            </button>
            <span className="text-h5 text-neutral-500">{fmtDateTime(c.executed_at)}</span>
            <span className="text-h5 text-neutral-700 truncate" title={captureLabel(c)}>
              {captureLabel(c)}
            </span>
            <div className="flex flex-row items-center justify-between gap-2">
              <span className={`text-h5 font-medium ${c.ok ? "text-neutral-600" : "text-red-600"}`}>{opLabel(c.op)}</span>
              <button type="button" onClick={() => onOpenCommand(c.cmd_id)} className="text-h5 text-indigo-600 hover:underline">
                Voir la commande
              </button>
            </div>
          </div>
        ))}
      </div>
      {hasMore && (
        <button type="button" onClick={onMore} className="text-h4 text-indigo-600 hover:underline w-fit">
          Charger plus
        </button>
      )}
    </div>
  );
}

export function AlphaRobot() {
  const [orgs, setOrgs] = useState<{ id: string; name: string }[]>([]);
  const [orgId, setOrgId] = useState(HERIC_ORG_ID);
  const [status, setStatus] = useState<AlphaRobotStatus | null>(null);
  const [items, setItems] = useState<AlphaRobotCommand[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [onlyErrors, setOnlyErrors] = useState(false);
  const countRef = useRef(PAGE);
  const [view, setView] = useState<"commands" | "captures">("commands");
  const [captures, setCaptures] = useState<AlphaRobotCapture[] | null>(null);
  const [capturesMore, setCapturesMore] = useState(false);
  const capCountRef = useRef(CAPTURE_PAGE);
  const [zoom, setZoom] = useState<Zoom | null>(null);
  const [screen, setScreen] = useState<AlphaRobotScreen | null>(null);
  const [requesting, setRequesting] = useState(false);
  const activeOrg = useRef(orgId);
  activeOrg.current = orgId;
  const mounted = useRef(true);
  const polling = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    proxyAdminApi
      .list()
      .then((list) => {
        const o = list.map((p) => ({ id: p.org_id, name: p.org_name || p.org_id }));
        setOrgs(o);
        if (o.length && !o.some((x) => x.id === HERIC_ORG_ID)) setOrgId(o[0].id);
      })
      .catch(() => undefined);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [st, page, caps, scr] = await Promise.all([
        proxyAdminApi.getAlphaRobotStatus(orgId),
        proxyAdminApi.listAlphaRobotCommands(orgId, Math.min(countRef.current, 200), 0),
        proxyAdminApi.listAlphaRobotCaptures(orgId, Math.min(capCountRef.current, 100), 0),
        proxyAdminApi.getAlphaRobotScreen(orgId),
      ]);
      setScreen(scr);
      setStatus(st);
      setCaptures(caps.items);
      setCapturesMore(caps.has_more);
      setItems(page.items);
      setTotal(page.total);
      setHasMore(page.has_more);
      setRefreshedAt(new Date());
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Lecture du robot Alpha impossible" });
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    countRef.current = PAGE;
    capCountRef.current = CAPTURE_PAGE;
    setItems([]);
    setStatus(null);
    setCaptures(null);
    setScreen(null);
    refresh();
    const t = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const loadMore = async () => {
    try {
      const page = await proxyAdminApi.listAlphaRobotCommands(orgId, PAGE, items.length);
      setItems((prev) => [...prev, ...page.items.filter((i) => !prev.some((p) => p.id === i.id))]);
      setHasMore(page.has_more);
      countRef.current = items.length + page.items.length;
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Lecture impossible" });
    }
  };

  // A new picture of the screen every 30 s while the page is open and the tab visible; the
  // interval dies with the page. After a request, read the result every 5 s for a minute.
  const requestScreen = useCallback(async (manual: boolean) => {
    if (manual) setRequesting(true);
    let owner = false;
    try {
      const { cmd_id } = await proxyAdminApi.requestAlphaRobotScreen(orgId);
      // one reading loop at a time, every 5 s for a minute at most (FinOps 2026-10-17)
      if (polling.current) return;
      polling.current = true;
      owner = true;
      const until = Date.now() + 60000;
      while (Date.now() < until) {
        await new Promise((r) => setTimeout(r, 5000));
        if (!mounted.current || activeOrg.current !== orgId) break;
        if (document.visibilityState !== "visible" && !manual) break;
        const scr = await proxyAdminApi.getAlphaRobotScreen(orgId);
        setScreen(scr);
        if (scr.last_attempt?.cmd_id === cmd_id || scr.capture?.cmd_id === cmd_id) break;
      }
    } catch (e: any) {
      if (manual) showNotification({ type: "error", message: e?.response?.data?.detail || "Capture d'écran impossible" });
    } finally {
      if (owner) polling.current = false;
      if (manual) setRequesting(false);
    }
  }, [orgId]);

  useEffect(() => {
    let stopped = false;
    const tick = () => {
      if (!stopped && document.visibilityState === "visible") requestScreen(false);
    };
    tick();
    const t = setInterval(tick, SCREEN_EVERY_MS);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [requestScreen]);

  const loadMoreCaptures = async () => {
    try {
      const have = captures?.length || 0;
      const page = await proxyAdminApi.listAlphaRobotCaptures(orgId, CAPTURE_PAGE, have);
      setCaptures((prev) => {
        const cur = prev || [];
        const seen = new Set(cur.map((c) => `${c.cmd_id}/${c.index}`));
        return [...cur, ...page.items.filter((c) => !seen.has(`${c.cmd_id}/${c.index}`))];
      });
      setCapturesMore(page.has_more);
      capCountRef.current = have + page.items.length;
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Lecture impossible" });
    }
  };

  const shown = onlyErrors ? items.filter((i) => !i.ok) : items;

  return (
    <div className="flex flex-col gap-5 p-8 max-w-1p2k w-full">
      <div className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="text-hx font-semibold text-neutral-800">Robot Alpha</span>
          <span className="text-h4 text-neutral-500">
            Le robot qui pilote l'écran d'Alpha sur le serveur du garage : son état et chaque commande exécutée.
          </span>
        </div>
        <div className="flex flex-row items-center gap-3">
          {orgs.length > 1 && (
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="border border-neutral-200 rounded-lg px-2 py-1 text-h4 bg-white"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          )}
          <span className="text-h5 text-neutral-400">
            {refreshedAt ? `Actualisé à ${refreshedAt.toLocaleTimeString("fr-FR")}` : ""}
          </span>
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="px-3 py-1 rounded-lg border border-neutral-200 text-h4 text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
          >
            {loading ? "…" : "Actualiser"}
          </button>
        </div>
      </div>

      <ScreenCard orgId={orgId} screen={screen} requesting={requesting} onRefresh={() => requestScreen(true)} onZoom={setZoom} />

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start">
        <StatusCard status={status} latest={items[0]} hidePendingId={screen?.pending_id} />
        <LastCapture
          orgId={orgId}
          capture={captures === null ? undefined : captures[0] || null}
          onZoom={setZoom}
          onOpenCommand={setOpenId}
        />
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl flex flex-col">
        <div className="flex flex-row items-center justify-between px-4 py-3 border-b border-neutral-100">
          <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-1 w-fit">
            {([
              ["commands", total != null ? `Commandes (${total})` : "Commandes"],
              ["captures", "Captures"],
            ] as const).map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={`px-3 py-1 rounded-md text-h4 transition-colors ${
                  view === v ? "bg-white text-neutral-800 font-medium shadow-sm" : "text-neutral-500 hover:text-neutral-700"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {view === "commands" && (
          <label className="flex flex-row items-center gap-2 text-h4 text-neutral-600 cursor-pointer">
            <input type="checkbox" checked={onlyErrors} onChange={(e) => setOnlyErrors(e.target.checked)} />
            Erreurs seulement
          </label>
          )}
        </div>
        {view === "captures" && (
          <CapturesGallery
            orgId={orgId}
            captures={captures || []}
            hasMore={capturesMore}
            onMore={loadMoreCaptures}
            onZoom={setZoom}
            onOpenCommand={setOpenId}
          />
        )}
        {view === "commands" && shown.length === 0 && (
          <div className="px-4 py-6 text-h4 text-neutral-400">{loading ? "Chargement…" : "Aucune commande."}</div>
        )}
        {view === "commands" && shown.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setOpenId(c.id)}
            className="flex flex-row items-center gap-3 px-4 py-2 border-b border-neutral-50 hover:bg-neutral-50 text-left w-full"
          >
            <span className="text-h4 text-neutral-500 w-125 shrink-0" title={c.executed_at || ""}>
              {fmtDateTime(c.executed_at)}
            </span>
            <span className="text-h4 font-medium text-neutral-800 w-150 shrink-0">{opLabel(c.op)}</span>
            <span className="w-60 shrink-0">{c.ok ? <Pill tone="green">OK</Pill> : <Pill tone="red">Erreur</Pill>}</span>
            <span className={`text-h4 flex-1 min-w-0 truncate ${c.ok ? "text-neutral-600" : "text-red-600"}`} title={c.summary}>
              {c.summary}
            </span>
            {c.image_count > 0 && (
              <span className="text-h5 text-neutral-400 shrink-0" title="Captures">
                {c.image_count} capture{c.image_count > 1 ? "s" : ""}
              </span>
            )}
            <span className="text-h5 text-neutral-400 w-60 text-right shrink-0">{fmtMs(c.elapsed_ms)}</span>
          </button>
        ))}
        {view === "commands" && hasMore && (
          <button type="button" onClick={loadMore} className="px-4 py-3 text-h4 text-indigo-600 hover:bg-neutral-50 text-left">
            Charger plus
          </button>
        )}
      </div>

      {openId && <DetailModal orgId={orgId} cmdId={openId} onClose={() => setOpenId(null)} />}
      {zoom && <Lightbox zoom={zoom} onClose={() => setZoom(null)} />}
    </div>
  );
}
